import React, { useRef } from 'react';
import styled from 'styled-components';

import useUI from 'hooks/use-ui';
import { BlackSparkleIcon } from 'src/assets/black-sparkle-icon';
import {
  ConciergeRing,
  conciergeBadgeCss,
  conciergePillCss,
  conciergeSurfaceCss,
} from 'src/concierge/concierge-card-chrome';
import { ConciergeVariables } from 'src/concierge/concierge-variables';
import { useConciergeThemeScope } from 'src/concierge/use-concierge-theme-scope';
import type { ConciergeMessageSeed } from 'src/concierge/contract/actions';
import { useIsConciergeEnabled } from 'src/concierge/use-is-concierge-enabled';
import { useConciergeDisplayName } from 'src/concierge/use-concierge-display-name';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { useSeededConciergeNavigate } from 'src/dispensary/core-menu/components/header/concierge-nav-button/use-concierge-navigate';

const SEED: ConciergeMessageSeed = { kind: 'product_search', message: 'What products do you recommend for me?' };

export function RecommendationsConciergeCard(): JSX.Element | null {
  const { dispensary } = useDispensary();
  const UI = useUI();
  const displayName = useConciergeDisplayName();
  const conciergeEnabled = useIsConciergeEnabled(dispensary, UI);
  const openWithSeed = useSeededConciergeNavigate();
  const { overridesCss, scopeProps } = useConciergeThemeScope();
  const cardRef = useRef<HTMLElement>(null);

  if (!conciergeEnabled || !dispensary?.id) {
    return null;
  }

  const buttonLabel = displayName ? `Ask ${displayName}` : 'Ask AI Concierge';

  return (
    <>
      <ConciergeVariables $tenantOverrides={overridesCss} />
      <Slide role='group' aria-roledescription='slide' aria-label='AI Concierge'>
        <Card ref={cardRef} data-testid='recommendations-concierge-card' {...scopeProps}>
          <ConciergeRing targetRef={cardRef} testId='recommendations-concierge-card-ring' />
          <Content>
            <Sparkle aria-hidden='true'>
              <BlackSparkleIcon width={24} height={24} fill='currentColor' />
            </Sparkle>
            <Headline>See More Recommendations</Headline>
            <Subtext>Check out a special list of curated products based on your shopping history</Subtext>
            <Cta type='button' onClick={() => openWithSeed(SEED, 'recommendations_home')}>
              {buttonLabel}
            </Cta>
          </Content>
        </Card>
      </Slide>
    </>
  );
}

const Slide = styled.div`
  display: flex;
  flex: 0 0 auto;
  width: var(--dcm-card-context-width);
`;

const Card = styled.div`
  ${conciergeSurfaceCss}
  align-items: center;
  border-radius: var(--dcm-radius-cards, 8px);
  display: flex;
  height: 100%;
  isolation: isolate;
  padding: 22px 18px;
  position: relative;
  width: 100%;
`;

const Content = styled.span`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
  position: relative;
  text-align: left;
  width: 100%;
`;

const Sparkle = styled.span`
  ${conciergeBadgeCss}
  border-radius: var(--dcm-recommendations-concierge-card-icon-border-radius, 50%);
`;

const Headline = styled.span`
  font-family: var(--dcm-font-primary, inherit);
  font-size: var(--dcm-font-size-lg, 16px);
  font-weight: var(--dcm-font-weight-bold, 700);
  line-height: 1.25;
`;

const Subtext = styled.span`
  color: var(--dcm-color-text-secondary, #889591);
  font-size: var(--dcm-font-size-sm, 13px);
`;

const Cta = styled.button`
  ${conciergePillCss}
`;
